import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule, forwardRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { NgSelectModule } from '@ng-select/ng-select';
import { EditorModule } from '@tinymce/tinymce-angular';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { NgxLoadingModule } from 'ngx-loading';
import { MonacoEditorModule } from 'ngx-monaco-editor';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { NgxViewerModule } from 'ngx-viewer';
import 'tinymce/icons/default';
import { AlertComponent } from '../dialog/alert/alert.component';
import { ConfirmComponent } from '../dialog/confirm/confirm.component';
import { DialogModule } from '../dialog/dialog.module';
import { MultipleButtonComponent } from '../dialog/multiple-button/multiple-button.component';
import { MapboxModule } from './mapbox/mapbox.module';
import { MarkdownComponent } from '../question/markdown/markdown.component';
import { SearchFilterComponent } from '../search.filter/search-filter.component';
import { VersionCloneModalComponent } from '../version/version-clone-modal/version-clone-modal.component';
import { DatepickerInputComponent } from './_components/datepicker-input/datepicker-input.component';
import { ImgAjaxComponent } from './_components/img-ajax/img-ajax.component';
import { ListViewComponent } from './_components/list-view/list-view.component';
import { SendEmailModalComponent } from './_components/send-email-modal/send-email-modal.component';
import { TabsComponent } from './_components/tabs/tabs.component';
import { ViewEmailModalComponent } from './_components/view-email-modal/view-email-modal.component';
import { ExtractTextPipe } from './extract-text.pipe';
import { ForScrollDirective } from './for-scroll.directive';
import { LoaderService } from './loader.service';
import { MergeAnswersService } from './merge.answers.service';
import { InspectionFilterCardsComponent } from './_components/inspection-filter-cards/inspection-filter-cards.component';
import { AdminAssignmentInspectionComponent } from './_components/admin-assignment/admin-assignmentcomponent';

import { MatTreeModule } from '@angular/material/tree';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatStepperModule } from '@angular/material/stepper';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';

import { ExtensionGrantedDeadlinesComponent } from '../inspection/extension-granted-deadlines/extension-granted-deadlines.component';
import { StripHtmlPipe } from './_components/flag-group-template/strip-html.pipe';
import { FlagGroupTemplateComponent } from './_components/flag-group-template/flag-group-template.component';
import { FilterPipe } from './_components/flag-group-template/filter.pipe';
import { PictureViewerComponent } from '../inspection/picture-viewer/picture-viewer.component';
import { AddFlagComponent } from '../inspection/add.flag/add.flag.component';
import { EditFlagComponent } from '../inspection/edit.flag/edit.flag.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ModalModule.forRoot(),
    TypeaheadModule.forRoot(),
    PaginationModule.forRoot(),
    BsDropdownModule.forRoot(),
    BsDatepickerModule.forRoot(),
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-center',
      closeButton: true,
      timeOut: 5000,
      extendedTimeOut: 2000,
      progressBar: false,
      preventDuplicates: true,
      newestOnTop: false,
    }),
    DragDropModule,
    CollapseModule.forRoot(),
    DialogModule,
    LeafletModule.forRoot(),
    NgSelectModule,
    NgxViewerModule,
    MonacoEditorModule.forRoot(),
    MapboxModule,
    NgxLoadingModule,
    PerfectScrollbarModule,
    EditorModule,
    RouterModule,
    NgxDaterangepickerMd.forRoot(),
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatGridListModule,
    MatStepperModule,
    MatExpansionModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDialogModule,
    MatTooltipModule,
    MatSnackBarModule,
  ],
  declarations: [
    ImgAjaxComponent,
    MarkdownComponent,
    SearchFilterComponent,
    VersionCloneModalComponent,
    SendEmailModalComponent,
    ForScrollDirective,
    ViewEmailModalComponent,
    ListViewComponent,
    TabsComponent,
    DatepickerInputComponent,
    ExtractTextPipe,
    InspectionFilterCardsComponent,
    AdminAssignmentInspectionComponent,
    FlagGroupTemplateComponent,
    StripHtmlPipe,
    ExtensionGrantedDeadlinesComponent,
    FilterPipe,
    PictureViewerComponent,
    AddFlagComponent,
    EditFlagComponent,
  ],
  exports: [
    AlertComponent,
    ConfirmComponent,
    MultipleButtonComponent,
    CommonModule,
    TypeaheadModule,
    PaginationModule,
    BsDropdownModule,
    CollapseModule,
    ModalModule,
    FormsModule,
    ReactiveFormsModule,
    ToastrModule,
    BsDatepickerModule,
    DragDropModule,
    ImgAjaxComponent,
    MarkdownComponent,
    LeafletModule,
    NgSelectModule,
    NgxViewerModule,
    MonacoEditorModule,
    MapboxModule,
    SearchFilterComponent,
    NgxLoadingModule,
    VersionCloneModalComponent,
    SendEmailModalComponent,
    ForScrollDirective,
    ListViewComponent,
    TabsComponent,
    DatepickerInputComponent,
    ExtractTextPipe,
    InspectionFilterCardsComponent,
    AdminAssignmentInspectionComponent,
    FlagGroupTemplateComponent,
    ExtensionGrantedDeadlinesComponent,
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatMenuModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatGridListModule,
    MatStepperModule,
    MatExpansionModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatDialogModule,
    MatTooltipModule,
    MatSnackBarModule,
    PerfectScrollbarModule,
    PictureViewerComponent,
    AddFlagComponent,
    EditFlagComponent,
    StripHtmlPipe,
  ],
  providers: [LoaderService, MergeAnswersService],
})
export class SharedModule {}
